import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, type PropsWithChildren } from 'react';
import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';
import { dataClient, RemoteDataClient, useRemoteQuery, type QueryOptions, type RemoteKey, type Snapshot } from './dataClient';

export type QueryKey = RemoteKey;
type QueryFilter = { queryKey?: QueryKey; exact?: boolean; refetchType?: 'none' | 'active' | 'all'; type?: 'active' | 'all' };
type QueryDefinition<T> = QueryOptions<T> & {
  placeholderData?: T | ((previous: T | undefined, previousQuery?: { queryKey: QueryKey }) => T | undefined);
  initialData?: T;
  retry?: boolean | number;
  refetchIntervalInBackground?: boolean;
  refetchOnWindowFocus?: boolean;
  select?: (data: T) => unknown;
};
type MutationRecord = { id: number; mutationKey?: QueryKey; status: 'pending' | 'success' | 'error'; variables: unknown };
type MutationStore = { records: MutationRecord[] };
type QueryCacheEvent = {
  type: 'added' | 'updated' | 'removed';
  action: { type: 'success' | 'error' | 'fetch' | 'invalidate' };
  query: { queryKey: QueryKey; state: {
    data: unknown; dataUpdatedAt: number; isInvalidated: boolean;
    status: Snapshot<unknown>['status']; fetchStatus: Snapshot<unknown>['fetchStatus'];
  } };
};
type MutationCacheEvent = { type: 'updated'; action: { type: MutationRecord['status'] } };

const matches = (key: QueryKey, filter: QueryFilter = {}) => {
  const prefix = filter.queryKey ?? [];
  if (filter.exact && key.length !== prefix.length) return false;
  return prefix.every((part, index) => JSON.stringify(part) === JSON.stringify(key[index]));
};
let nextMutationId = 0;

/** Shared remote data and mutation state, both stored in Zustand. */
export class QueryClient {
  readonly remote: RemoteDataClient;
  readonly mutations = createStore<MutationStore>()(() => ({ records: [] }));
  constructor(_options: unknown = {}, remote?: RemoteDataClient) {
    this.remote = remote ?? new RemoteDataClient();
  }
  getQueryData<T>(key: QueryKey): T | undefined { return this.remote.get<T>(key).data; }
  getQueryState<T>(key: QueryKey) {
    const snapshot = this.remote.get<T>(key);
    if (!snapshot.updatedAt && snapshot.data === undefined && snapshot.status === 'pending') return undefined;
    return { data: snapshot.data, dataUpdatedAt: snapshot.updatedAt, isInvalidated: snapshot.invalidated,
      status: snapshot.status, fetchStatus: snapshot.fetchStatus };
  }
  setQueryData<T>(key: QueryKey, value: T | ((previous: T | undefined) => T | undefined), options?: { updatedAt?: number }) {
    this.remote.set(key, value, options?.updatedAt);
    return this.getQueryData<T>(key);
  }
  setQueriesData<T>(filter: QueryFilter, value: T | ((previous: T | undefined) => T | undefined)) {
    return this.remote.entries().filter(entry => matches(entry.queryKey, filter)).map(entry =>
      [entry.queryKey, this.setQueryData<T>(entry.queryKey, value)] as const);
  }
  async fetchQuery<T>(options: QueryDefinition<T>): Promise<T> {
    const value = await this.remote.load({ ...options, staleTime: options.staleTime ?? Infinity }, false, true);
    return value as T;
  }
  async invalidateQueries(filter: QueryFilter = {}) {
    if (filter.exact && filter.queryKey) {
      const key = filter.queryKey;
      const snapshot = this.remote.get(key);
      if (snapshot.updatedAt || snapshot.data !== undefined) this.remote.invalidate(key, filter.refetchType !== 'none', true);
    } else this.remote.invalidate(filter.queryKey ?? [], filter.refetchType !== 'none');
    return undefined;
  }
  async refetchQueries(filter: QueryFilter = {}) {
    await Promise.all(this.remote.activeOptions(filter.queryKey ?? [], filter.exact)
      .map(options => this.remote.load(options, true)));
  }
  async cancelQueries(filter: QueryFilter = {}) {
    this.remote.cancel(filter.queryKey ?? [], filter.exact);
  }
  removeQueries(filter: QueryFilter = {}) { this.remote.remove(filter.queryKey ?? [], filter.exact); }
  isMutating() { return this.mutations.getState().records.filter(item => item.status === 'pending').length; }
  clear() { this.remote.clear(); this.mutations.setState({ records: [] }); }
  getQueryCache() {
    return { subscribe: (listener: (event: QueryCacheEvent) => void) =>
      this.remote.store.subscribe((state, previous) => {
        for (const key of new Set([...Object.keys(state.queries), ...Object.keys(previous.queries)])) {
          const next = state.queries[key];
          const old = previous.queries[key];
          if (next === old) continue;
          const snapshot = next ?? old;
          const type = !next ? 'removed' : !old ? 'added' : 'updated';
          const action = next?.invalidated && !old?.invalidated ? 'invalidate'
            : next?.status === 'error' ? 'error'
            : next?.status === 'success' && (next.updatedAt !== old?.updatedAt || next.data !== old?.data ||
              (next.fetchStatus==='idle'&&old?.fetchStatus==='fetching')) ? 'success' : 'fetch';
          const event = { type, action: { type: action }, query: { queryKey: JSON.parse(key) as QueryKey,
            state: { data: snapshot.data, dataUpdatedAt: snapshot.updatedAt, isInvalidated: snapshot.invalidated,
              status: snapshot.status, fetchStatus: snapshot.fetchStatus } } } as QueryCacheEvent;
          listener(event);
          if (type === 'added' && action === 'success') listener({ ...event, type: 'updated' });
        }
      }) };
  }
  getMutationCache() {
    return {
      subscribe: (listener: (event: MutationCacheEvent) => void) => this.mutations.subscribe((state, previous) => {
        for (const record of state.records) {
          const old = previous.records.find(item => item.id === record.id);
          if (!old || old.status !== record.status) listener({ type: 'updated', action: { type: record.status } });
        }
      }),
      build: <T>(_: QueryClient, options: { mutationFn: (variables: unknown) => Promise<T> }) => ({
        execute: (variables: unknown) => this.executeMutation(options.mutationFn, variables),
      }),
    };
  }
  async executeMutation<T, V>(fn: (variables: V) => Promise<T>, variables: V, mutationKey?: QueryKey) {
    const generation=this.remote.store.getState().generation;
    const id = ++nextMutationId;
    this.mutations.setState(state => ({ records: [...state.records, { id, mutationKey, variables, status: 'pending' }] }));
    try {
      const data = await fn(variables);
      if(generation!==this.remote.store.getState().generation)throw new DOMException('The account or permissions changed.','AbortError');
      this.finishMutation(id,'success');
      return data;
    } catch (error) {
      this.finishMutation(id,'error');
      throw error;
    }
  }
  private finishMutation(id:number,status:'success'|'error') {
    this.mutations.setState(state=>{
      const records=state.records.map(item=>item.id===id?{...item,status,variables:undefined}:item);
      const keep=new Set(records.filter(item=>item.status!=='pending').slice(-32).map(item=>item.id));
      return {records:records.filter(item=>item.status==='pending'||keep.has(item.id))};
    });
  }
}

export const appQueryClient = new QueryClient({}, dataClient);
const ClientContext = createContext<QueryClient>(appQueryClient);
export function QueryClientProvider({ client, children }: PropsWithChildren<{ client: QueryClient }>) {
  return createElement(ClientContext.Provider, { value: client }, children);
}
export function useQueryClient() { return useContext(ClientContext); }

export function useQuery<T>(options: QueryDefinition<T>) {
  const client = useQueryClient();
  const generation=useStore(client.remote.store,state=>state.generation);
  if (options.initialData !== undefined && client.getQueryData(options.queryKey) === undefined)
    client.setQueryData(options.queryKey, options.initialData);
  const query = useRemoteQuery({ ...options, staleTime: options.staleTime ?? 30_000 }, client.remote);
  const previous = useRef<{ key: QueryKey; data: T | undefined; generation:number }>({ key: options.queryKey, data: undefined,generation });
  if(previous.current.generation!==generation)previous.current={key:options.queryKey,data:undefined,generation};
  const placeholder = options.placeholderData;
  const data = query.data ?? (typeof placeholder === 'function'
    ? (placeholder as (data: T | undefined, query: { queryKey: QueryKey }) => T | undefined)(
      previous.current.data, { queryKey: previous.current.key },
    )
    : placeholder);
  if (query.data !== undefined) previous.current = { key: options.queryKey, data: query.data,generation };
  return { ...query, data, isPending: query.status === 'pending', isError: query.status === 'error',
    isRefetching: query.isFetching && data !== undefined, isPlaceholderData: query.data === undefined && data !== undefined };
}

export function useQueries<T>({ queries }: { queries: QueryDefinition<T>[] }) {
  const client = useQueryClient();
  const signature = JSON.stringify(queries.map(item => [item.queryKey, item.enabled]));
  const latest = useRef(queries);
  latest.current = queries;
  const snapshots = useStore(client.remote.store, state => state.queries);
  const generation=useStore(client.remote.store,state=>state.generation);
  useEffect(() => latest.current.map(item => client.remote.observe({
    ...item, staleTime: item.staleTime ?? 30_000, queryFn: context => {
      const current = latest.current.find(candidate => JSON.stringify(candidate.queryKey) === JSON.stringify(item.queryKey));
      return (current ?? item).queryFn(context);
    },
  })).reduce<() => void>((cleanup, next) => () => { cleanup(); next(); }, () => {}), [client, signature,generation]);
  return queries.map(item => {
    const snapshot = snapshots[client.remote.key(item.queryKey)];
    return { data: snapshot?.data as T | undefined, error: snapshot?.error ?? null,
      isLoading: item.enabled !== false && (!snapshot || snapshot.status === 'pending') && snapshot?.data === undefined,
      isFetching: snapshot?.fetchStatus === 'fetching', isSuccess: snapshot?.status === 'success',
      status: snapshot?.status ?? 'pending' };
  });
}

type MutationOptions<TData, TVariables, TContext> = {
  mutationKey?: QueryKey;
  mutationFn: (variables: TVariables) => Promise<TData>;
  onMutate?: (variables: TVariables) => TContext | Promise<TContext>;
  onSuccess?: (data: TData, variables: TVariables, context: TContext | undefined) => unknown;
  onError?: (error: Error, variables: TVariables, context: TContext | undefined) => unknown;
  onSettled?: (data: TData | undefined, error: Error | null, variables: TVariables, context: TContext | undefined) => unknown;
};
type PerCall<TData, TVariables> = {
  onSuccess?: (data: TData, variables: TVariables) => unknown;
  onError?: (error: Error, variables: TVariables) => unknown;
  onSettled?: (data: TData | undefined, error: Error | null, variables: TVariables) => unknown;
};
export type UseMutationResult<TData = unknown, TError = Error, TVariables = void, _TContext = unknown> = {
  mutate: (variables: TVariables, options?: PerCall<TData, TVariables>) => void;
  mutateAsync: (variables: TVariables, options?: PerCall<TData, TVariables>) => Promise<TData>;
  isPending: boolean; isError: boolean; isSuccess: boolean;
  data: TData | undefined; error: TError | null;
  reset: () => void;
};
export function useMutation<TData, TError = Error, TVariables = void, TContext = unknown>(
  options: MutationOptions<TData, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const client = useQueryClient();
  const latest = useRef(options);
  latest.current = options;
  const local = useRef(createStore<{
    pending: number; data?: TData; error: TError | null; status: 'idle' | 'success' | 'error';
  }>()(() => ({ pending: 0, error: null, status: 'idle' })));
  const state = useStore(local.current);
  const mutateAsync = useCallback(async (variables: TVariables, perCall?: PerCall<TData, TVariables>) => {
    const current = latest.current;
    const generation = client.remote.store.getState().generation;
    const assertCurrent = () => {
      if (generation !== client.remote.store.getState().generation)
        throw new DOMException('The account or permissions changed.', 'AbortError');
    };
    let context: TContext | undefined;
    local.current.setState(value => ({ ...value, pending: value.pending + 1, error: null, status: 'idle' }));
    try {
      context = await current.onMutate?.(variables);
      assertCurrent();
      const data = await client.executeMutation(current.mutationFn, variables, current.mutationKey);
      assertCurrent();
      await current.onSuccess?.(data, variables, context);
      assertCurrent();
      await perCall?.onSuccess?.(data, variables);
      assertCurrent();
      local.current.setState(value => ({ ...value, pending: Math.max(0, value.pending - 1), data, status: 'success' }));
      await current.onSettled?.(data, null, variables, context);
      assertCurrent();
      await perCall?.onSettled?.(data, null, variables);
      return data;
    } catch (caught) {
      const error = caught instanceof Error ? caught : new Error(String(caught));
      if (generation !== client.remote.store.getState().generation) {
        local.current.setState(value => ({ ...value, pending: Math.max(0, value.pending - 1), data: undefined, error: null, status: 'idle' }));
        throw new DOMException('The account or permissions changed.', 'AbortError');
      }
      await current.onError?.(error, variables, context);
      assertCurrent();
      await perCall?.onError?.(error, variables);
      local.current.setState(value => ({ ...value, pending: Math.max(0, value.pending - 1), error: error as TError, status: 'error' }));
      await current.onSettled?.(undefined, error, variables, context);
      assertCurrent();
      await perCall?.onSettled?.(undefined, error, variables);
      throw error;
    }
  }, [client]);
  const mutate = useCallback((variables: TVariables, perCall?: PerCall<TData, TVariables>) => {
    void mutateAsync(variables, perCall).catch(() => {});
  }, [mutateAsync]);
  return useMemo(() => ({
    mutate, mutateAsync, isPending: state.pending > 0, isError: state.status === 'error',
    isSuccess: state.status === 'success', data: state.data, error: state.error,
    reset: () => local.current.setState({ pending: 0, error: null, status: 'idle', data: undefined }),
  }), [mutate, mutateAsync, state]);
}

export function useMutationState<T = unknown>(options: {
  filters?: { mutationKey?: QueryKey; status?: MutationRecord['status'] };
  select?: (mutation: { state: { variables: unknown; status: MutationRecord['status'] } }) => T;
}) {
  const client = useQueryClient();
  const records = useStore(client.mutations, state => state.records);
  return records.filter(item => (!options.filters?.status || item.status === options.filters.status) &&
    (!options.filters?.mutationKey || matches(item.mutationKey ?? [], { queryKey: options.filters.mutationKey })))
    .map(item => options.select ? options.select({ state: { variables: item.variables, status: item.status } }) : item as T);
}

export class QueryObserver<T> {
  constructor(private readonly client: QueryClient, private readonly options: QueryDefinition<T>) {}
  subscribe(listener: () => void) {
    const stop = this.client.remote.observe(this.options);
    const unsubscribe = this.client.remote.store.subscribe(listener);
    return () => { stop(); unsubscribe(); };
  }
  getCurrentResult() {
    const snapshot = this.client.remote.get<T>(this.options.queryKey);
    return { data: snapshot.data, error: snapshot.error, status: snapshot.status,
      isFetching: snapshot.fetchStatus === 'fetching',
      isLoading: snapshot.data === undefined && snapshot.status === 'pending' };
  }
}
